import { SectionSkeleton } from './SectionSkeleton';
import { CycleChipNav } from './CycleChipNav';
import { CardErrorState } from './CardErrorState';
import { HeroCard } from './HeroCard';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

/**
 * Casca partilhada dos heroes de ciclo — `.pen` → `ds/card/cycle-spend-hero`,
 * `--tight` e `--income`.
 *
 * Ao contrário da versão anterior, **é** o cartão: o `.pen` põe o `cycle-nav`
 * dentro do gradiente, seguido de `head`, `bar-block` e `foot`, todos irmãos
 * com `gap $space-5`. O `foot` tem `padding-top $space-4` e um traço superior
 * `$on-hero-track`.
 *
 * O master ganhou também um bloco `error` (chip + «Não foi possível carregar o
 * ciclo agora» + «Tentar novamente»), com a mesma casca em gradiente.
 *
 * @param {{
 *   cycles?: object[],
 *   cycleIndex?: number,
 *   onCycleIndexChange?: (index: number) => void,
 *   chipLabel: string,
 *   prevAriaLabel: string,
 *   nextAriaLabel: string,
 *   chipTooltip?: string,
 *   isLoading?: boolean,
 *   isError?: boolean,
 *   onRetry?: () => void,
 *   variant?: 'hero' | 'tight',
 *   className?: string,
 *   children?: import('react').ReactNode,
 *   footer?: import('react').ReactNode,
 * }} props
 */
export function CycleHeroShell({
  cycles = [],
  cycleIndex = 0,
  onCycleIndexChange,
  chipLabel,
  prevAriaLabel,
  nextAriaLabel,
  chipTooltip,
  isLoading = false,
  isError = false,
  onRetry,
  variant = 'hero',
  className,
  children,
  footer,
}) {
  if (isLoading) {
    return <SectionSkeleton variant="hero" className={className} />;
  }

  if (isError) {
    /** `.pen` → `error`: mesma casca em gradiente, altura 320. */
    return (
      <section className={cn('flex w-full min-w-0 flex-col', className)}>
        <HeroCard variant={variant} className="min-h-80 justify-center">
          <CardErrorState
            tone="on-hero"
            message={pt.common.cycleLoadError}
            onRetry={onRetry}
          />
        </HeroCard>
      </section>
    );
  }

  return (
    <section className={cn('flex w-full min-w-0 flex-col', className)}>
      <HeroCard variant={variant}>
        <CycleChipNav
          tone="on-hero"
          cycles={cycles}
          cycleIndex={cycleIndex}
          onCycleIndexChange={onCycleIndexChange}
          label={chipLabel}
          prevAriaLabel={prevAriaLabel}
          nextAriaLabel={nextAriaLabel}
          tooltip={chipTooltip}
        />

        {children}

        {footer ? (
          <div className="flex min-w-0 flex-col border-t border-sage-50/20 pt-space-4">
            {footer}
          </div>
        ) : null}
      </HeroCard>
    </section>
  );
}

export default CycleHeroShell;
